import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { router } from '../../services/router.js';
import { ProjectStateController } from '../../services/project-state.js';
import { AuthStateController } from '../../services/auth-state.js';
import { cloudTerminalService, type CloudTerminalSessionInfo } from '../../services/cloud-terminal-service.js';
import type { RouteMatch } from '../../services/router.js';
import '../../pages/cp-page-login.js';
import '../../pages/cp-page-inbox.js';
import '../../pages/cp-page-jarvis.js';
import '../../pages/cp-page-approvals.js';
import '../../pages/cp-page-signals.js';
import '../../pages/cp-page-signal-sources.js';
import '../../pages/cp-page-goals.js';
import '../../pages/cp-page-decisions.js';
import '../../pages/cp-page-playbooks.js';
import '../reflection/cp-quick-note-modal.js';
import '../chat/cp-fab-menu.js';

@customElement('cp-app-shell')
export class CpAppShell extends LitElement {
  createRenderRoot() { return this; }

  @state() private _currentPath = router.currentPath;
  @state() private _currentComponent = router.getCurrentRoute()?.component ?? 'cp-page-board';
  @state() private _terminalOpen = false;
  @state() private _cloudTerminalOpen = false;
  @state() private _mobileSidebarOpen = false;
  @state() private _cloudTerminalSessions: CloudTerminalSessionInfo[] = [];
  @state() private _cloudTerminalActiveSessionId = '';
  @state() private _sidebarOpen = false;

  private _projectState = new ProjectStateController(this);
  private _authState = new AuthStateController(this);

  connectedCallback() {
    super.connectedCallback();
    router.addEventListener('route-changed', this._onRouteChanged);
    this.addEventListener('sidebar-nav-click', this._closeSidebar);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    router.removeEventListener('route-changed', this._onRouteChanged);
    this.removeEventListener('sidebar-nav-click', this._closeSidebar);
  }

  private _onRouteChanged = (e: Event) => {
    const detail = (e as CustomEvent).detail as { path: string; route?: RouteMatch['route']; params: Record<string, string> };
    this._currentPath = detail.path;
    this._currentComponent = detail.route?.component ?? 'cp-page-board';
  };

  private _getActiveSidebarPath(): string {
    // Strip sub-IDs but keep the base path for sidebar matching
    const segments = this._currentPath.split('/').filter(Boolean);
    if (segments.length > 0) {
      return '/' + segments[0];
    }
    return this._currentPath;
  }

  private _renderPage() {
    switch (this._currentComponent) {
      case 'cp-page-jarvis': return html`<cp-page-jarvis></cp-page-jarvis>`;
      case 'cp-page-approvals': return html`<cp-page-approvals></cp-page-approvals>`;
      case 'cp-page-board': return html`<cp-page-board></cp-page-board>`;
      case 'cp-page-projects': return html`<cp-page-projects></cp-page-projects>`;
      case 'cp-page-todos': return html`<cp-page-todos></cp-page-todos>`;
      case 'cp-page-lifeplan': return html`<cp-page-lifeplan></cp-page-lifeplan>`;
      case 'cp-morning-check': return html`<cp-morning-check></cp-morning-check>`;
      case 'cp-page-messages': return html`<cp-page-messages></cp-page-messages>`;
      case 'cp-page-ceo-chats': return html`<cp-page-ceo-chats></cp-page-ceo-chats>`;
      case 'cp-page-cron': return html`<cp-page-cron></cp-page-cron>`;
      case 'cp-page-breakdowns': return html`<cp-page-breakdowns></cp-page-breakdowns>`;
      case 'cp-page-inbox': return html`<cp-page-inbox></cp-page-inbox>`;
      case 'cp-page-signals': return html`<cp-page-signals></cp-page-signals>`;
      case 'cp-page-signal-sources': return html`<cp-page-signal-sources></cp-page-signal-sources>`;
      case 'cp-page-goals': return html`<cp-page-goals></cp-page-goals>`;
      case 'cp-page-decisions': return html`<cp-page-decisions></cp-page-decisions>`;
      case 'cp-page-playbooks': return html`<cp-page-playbooks></cp-page-playbooks>`;
      case 'cp-page-settings': return html`<cp-page-settings></cp-page-settings>`;
      case 'cp-page-team': return html`<cp-page-team></cp-page-team>`;
      case 'cp-page-voice-call': return html`<cp-page-voice-call></cp-page-voice-call>`;
      case 'cp-page-chat': return html`<cp-page-chat></cp-page-chat>`;
      case 'cp-page-people': return html`<cp-page-people></cp-page-people>`;
      case 'cp-page-agents': return html`<cp-page-agents></cp-page-agents>`;
      case 'cp-page-companies': return html`<cp-page-companies></cp-page-companies>`;
      case 'cp-page-company': return html`<cp-page-company></cp-page-company>`;
      case 'cp-page-conversations': return html`<cp-page-conversations></cp-page-conversations>`;
      case 'cp-page-founder-board': return html`<cp-page-founder-board></cp-page-founder-board>`;
      case 'cp-page-monitoring': return html`<cp-page-monitoring></cp-page-monitoring>`;
      case 'cp-page-skills': return html`<cp-page-skills></cp-page-skills>`;
      default: return html`<cp-page-board></cp-page-board>`;
    }
  }

  private _isProjectsRoute(): boolean {
    return this._currentPath.startsWith('/projects');
  }

  private _openQuickNote(): void {
    const modal = this.querySelector('cp-quick-note-modal');
    if (modal) (modal as import('../reflection/cp-quick-note-modal.js').CpQuickNoteModal).open();
  }

  private _toggleMobileSidebar(): void {
    this._mobileSidebarOpen = !this._mobileSidebarOpen;
  }

  private _closeMobileSidebar(): void {
    this._mobileSidebarOpen = false;
  }

  private _toggleTerminal(): void {
    this._terminalOpen = !this._terminalOpen;
  }

  private _toggleSidebar(): void {
    this._sidebarOpen = !this._sidebarOpen;
  }

  private _closeSidebar = (): void => {
    this._sidebarOpen = false;
  };

  // Cloud Terminal methods

  private _toggleCloudTerminal(): void {
    this._cloudTerminalOpen = !this._cloudTerminalOpen;
  }

  private _onCloudSidebarClose(): void {
    this._cloudTerminalOpen = false;
  }

  private _onCloudNewSession(): void {
    // Create a placeholder session in model-select state
    const placeholderId = `pending-${Date.now()}`;
    const placeholder: CloudTerminalSessionInfo = {
      id: placeholderId,
      type: 'shell',
      status: 'creating',
      createdAt: new Date().toISOString(),
    };
    this._cloudTerminalSessions = [...this._cloudTerminalSessions, placeholder];
    this._cloudTerminalActiveSessionId = placeholderId;
  }

  private _onCloudSessionSelect(e: CustomEvent<{ sessionId: string }>): void {
    this._cloudTerminalActiveSessionId = e.detail.sessionId;
  }

  private _onCloudSessionClose(e: CustomEvent<{ sessionId: string }>): void {
    const sessionId = e.detail.sessionId;
    cloudTerminalService.closeSession(sessionId);
    cloudTerminalService.removeSession(sessionId);

    const remaining = this._cloudTerminalSessions.filter(s => s.id !== sessionId);
    this._cloudTerminalSessions = remaining;

    if (this._cloudTerminalActiveSessionId === sessionId) {
      this._cloudTerminalActiveSessionId = remaining.length > 0 ? remaining[remaining.length - 1].id : '';
    }
  }

  private _onOpenProjectClaude(e: CustomEvent<{ projectPath: string; projectName: string }>): void {
    const { projectPath, projectName } = e.detail;

    this._cloudTerminalOpen = true;

    const placeholderId = `pending-${Date.now()}`;
    const placeholder: CloudTerminalSessionInfo = {
      id: placeholderId,
      type: 'claude',
      status: 'creating',
      createdAt: new Date().toISOString(),
      title: projectName,
      cwd: projectPath,
    };
    this._cloudTerminalSessions = [...this._cloudTerminalSessions, placeholder];
    this._cloudTerminalActiveSessionId = placeholderId;
  }

  private _onCloudSessionConnected(e: CustomEvent<CloudTerminalSessionInfo>): void {
    const newSession = e.detail;

    // Replace the placeholder "creating" session with the real one
    const sessions = this._cloudTerminalSessions.map(s =>
      s.status === 'creating' && s.id === this._cloudTerminalActiveSessionId
        ? newSession
        : s
    );
    this._cloudTerminalSessions = sessions;
    this._cloudTerminalActiveSessionId = newSession.id;
  }

  render() {
    // Auth guard: loading state
    if (this._authState.isLoading) {
      return html`
        <div class="cp-app-shell__auth-loading">
          <div class="cp-app-shell__auth-loading-spinner">
            <span>Laden...</span>
          </div>
        </div>
      `;
    }

    // Auth guard: not authenticated - show login page
    if (!this._authState.isAuthenticated) {
      return html`<cp-page-login .sessionExpired=${this._authState.sessionExpired}></cp-page-login>`;
    }

    const isProjects = this._isProjectsRoute();
    const activeProjectId = this._projectState.activeProject?.id ?? 0;
    const shellClass = `cp-app-shell${isProjects && this._terminalOpen ? ' cp-app-shell--terminal-open' : ''}${this._mobileSidebarOpen ? ' cp-app-shell--sidebar-open' : ''}`;

    const cloudToggleClass = `cp-app-shell__cloud-terminal-toggle${this._cloudTerminalOpen ? ' cp-app-shell__cloud-terminal-toggle--active' : ''}`;
    const sessionCount = this._cloudTerminalSessions.filter(s => s.status === 'running').length;

    return html`
      <div class="${shellClass}" @open-project-claude=${this._onOpenProjectClaude}>
        <cp-sidebar activePath=${this._getActiveSidebarPath()} ?open=${this._sidebarOpen}></cp-sidebar>
        <div class="cp-app-shell__backdrop ${this._sidebarOpen ? 'cp-app-shell__backdrop--visible' : ''}" @click=${this._closeSidebar}></div>
        <div class="cp-app-shell__main">
          <!-- Global Header with Quick Note + Cloud Terminal Toggle -->
          <div class="cp-app-shell__global-header">
            <button
              class="cp-app-shell__hamburger"
              @click=${this._toggleSidebar}
              title="Menu"
            >&#9776;</button>
            <div class="cp-app-shell__global-header-left">
              <span class="cp-app-shell__version" title="v${__APP_VERSION__} (${__BUILD_HASH__})">v${__APP_VERSION__} &middot; ${new Date(__BUILD_TIME__).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>
            </div>
            <button
              class="cp-app-shell__quick-note-btn"
              @click=${this._openQuickNote}
              title="Quick Note hinzufuegen"
            >&#128221;</button>
            <button
              class="${cloudToggleClass}"
              @click=${this._toggleCloudTerminal}
              title="${this._cloudTerminalOpen ? 'Cloud Terminal schliessen' : 'Cloud Terminal oeffnen'}"
            >
              &#9638;
              ${sessionCount > 0 ? html`
                <span class="cp-app-shell__session-badge">${sessionCount}</span>
              ` : nothing}
            </button>
          </div>

          ${isProjects ? html`
            <div class="cp-app-shell__project-bar">
              <cp-project-tabs></cp-project-tabs>
              <button
                class="cp-app-shell__terminal-toggle ${this._terminalOpen ? 'cp-app-shell__terminal-toggle--active' : ''}"
                @click=${this._toggleTerminal}
                title="${this._terminalOpen ? 'Terminal schliessen' : 'Terminal oeffnen'}"
              >&#9638;</button>
            </div>
            <cp-git-panel .projectId=${activeProjectId}></cp-git-panel>
          ` : nothing}
          <main class="cp-app-shell__content">
            ${this._renderPage()}
          </main>
        </div>
        ${isProjects && this._terminalOpen ? html`
          <div class="cp-app-shell__terminal-sidebar">
            <div class="cp-app-shell__terminal-header">
              <span>Terminal</span>
              <button
                class="cp-app-shell__terminal-close"
                @click=${this._toggleTerminal}
                title="Terminal schliessen"
              >&times;</button>
            </div>
            <cp-terminal-panel .projectId=${activeProjectId}></cp-terminal-panel>
          </div>
        ` : nothing}

        <!-- Cloud Terminal Sidebar (always in DOM, position:fixed) -->
        <cp-cloud-terminal-sidebar
          .open=${this._cloudTerminalOpen}
          .sessions=${this._cloudTerminalSessions}
          .activeSessionId=${this._cloudTerminalActiveSessionId}
          @sidebar-close=${this._onCloudSidebarClose}
          @new-session=${this._onCloudNewSession}
          @session-select=${this._onCloudSessionSelect}
          @session-close=${this._onCloudSessionClose}
          @session-connected=${this._onCloudSessionConnected}
        ></cp-cloud-terminal-sidebar>

        <!-- Quick Note Modal (Ctrl+Shift+N / Cmd+Shift+N) -->
        <cp-quick-note-modal></cp-quick-note-modal>

        <!-- FAB: Chat Quick-Access (hidden on chat page) -->
        ${!this._currentPath.startsWith('/chat') ? html`<cp-fab-menu></cp-fab-menu>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-app-shell': CpAppShell;
  }
}
